"use client";
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyerInfo, setBuyerInfo] = useState(null);

  // Buscar informações do comprador via API (bypass RLS)
  async function fetchBuyerInfo() {
    try {
      const response = await fetch("/api/auth/me");
      if (!response.ok) {
        console.log("Failed to fetch buyer info");
        return null;
      }
      const data = await response.json();
      return data.buyerInfo;
    } catch (err) {
      console.error("Error fetching buyer info:", err);
      return null;
    }
  }

  // Função para atualizar buyerInfo (exposta para componentes)
  async function refreshBuyerInfo() {
    const info = await fetchBuyerInfo();
    setBuyerInfo(info);
    return info;
  }

  // Logout
  async function logout() {
    try {
      // Limpar estado local primeiro
      setUser(null);
      setBuyerInfo(null);
      
      // Fazer signOut no Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
      }
      
      // Limpar localStorage do Supabase manualmente se necessário
      localStorage.removeItem('sb-qxjuzsxacyqpyozzdrqy-auth-token');
      
      // Redirecionar
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout error:", error);
      // Forçar redirecionamento mesmo com erro
      window.location.href = "/login";
    }
  }

  // Buscar sessão atual
  async function getSession() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUser(session.user);
        const info = await fetchBuyerInfo();
        setBuyerInfo(info);
      } else {
        setUser(null);
        setBuyerInfo(null);
      }
    } catch (error) {
      console.error("Error getting session:", error);
      setUser(null);
      setBuyerInfo(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getSession();

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event);
        if (session?.user) {
          setUser(session.user);
          const info = await fetchBuyerInfo();
          setBuyerInfo(info);
        } else {
          setUser(null);
          setBuyerInfo(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    user,
    buyerInfo,
    loading,
    logout,
    refreshBuyerInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}
